import { analyzeClassOverlap, slopHits, SLOP_MIN_HITS } from './classOverlapCheck';
import { calculateNEISBytes } from './neisBytes';
import { analyzeSentenceOverlap } from './sentenceOverlap';

/**
 * 반 점검표용: 문장 겹침(주 기준) + 첫머리·상투어(보조 'AI 티') 결과를 합치고,
 * 겹치는 문장 묶음마다 한 명(앞 번호)은 그대로 두고 나머지 학생만 고칠 대상으로 고른다.
 */
export const planClassOverlap = (texts = [], { contextText = '', category = '' } = {}) => {
  const overlap = analyzeSentenceOverlap(texts, { contextText, category });
  const aux = analyzeClassOverlap(texts, {});
  const keeperOf = new Map(overlap.groups.map((group) => [group.id, group.students[0]]));
  const targets = overlap.students
    .map((entry) => {
      const sentences = entry.sentences.filter((sentence) => keeperOf.get(sentence.clusterId) !== entry.index);
      if (sentences.length === 0) return null;
      const auxEntry = aux.students.find((item) => item.index === entry.index) || null;
      return { index: entry.index, sentences, opening: auxEntry?.opening || '', severity: entry.severity, maxPeople: entry.maxPeople };
    })
    .filter(Boolean);
  const openingCount = aux.students.filter((item) => item.reasons.includes('opening')).length;
  const slopCount = aux.students.filter((item) => item.reasons.includes('slop')).length;
  return { ...overlap, targets, aux: { openingCount, slopCount } };
};

const PREVIEW_CHARS = 22;
const preview = (text) => (text.length > PREVIEW_CHARS ? `${text.slice(0, PREVIEW_CHARS)}…` : text);

/** 한 줄 설명: '문장 앞부분…' 외 N문장 · N명과 같아요 */
export const describeOverlapTarget = (target) => {
  const first = target.sentences[0];
  const verb = first.tier === 'same' ? '같아요' : '거의 같아요';
  const more = target.sentences.length > 1 ? ` 외 ${target.sentences.length - 1}문장` : '';
  return `'${preview(first.text)}'${more} · ${first.people - 1}명과 ${verb}`;
};

const KIND_HINT = {
  activity: '활동 이름은 두고, 이 학생이 실제로 한 일과 결과 중심으로',
  generic: '누구에게나 쓸 수 있는 평가 표현 대신 학생 자료에 있는 구체적인 행동으로',
  individual: '이 학생 자료에만 있는 행동과 표현으로',
};

/** '겹치지 않게 고치기' 요청. 다른 학생 글은 넣지 않고, 이 학생 글에서 바꿀 문장만 알려 준다. */
export const buildOverlapFixInstruction = (target, { text = '', kinds = {}, category = '' } = {}) => {
  const lines = [
    '다른 학생 글과 같거나 거의 같은 문장이 있어요. 아래 문장만 바꿔 주세요.',
    '사실·활동·분량은 그대로 두고, 나머지 문장은 고치지 마세요.',
  ];
  (target?.sentences || []).slice(0, 6).forEach((sentence) => {
    const hint = KIND_HINT[kinds[sentence.clusterId]] || KIND_HINT.individual;
    lines.push(`- "${sentence.text}" → ${hint} 다시 쓰기`);
  });
  if (target?.opening) lines.push(`첫 문장을 '${target.opening}'처럼 시작하지 마세요.`);
  if (category === '진로') lines.push('첫 문장에서 희망 진로를 되풀이하지 마세요.');
  const counts = new Map();
  slopHits(text).forEach((word) => counts.set(word, (counts.get(word) || 0) + 1));
  if (slopHits(text).length >= SLOP_MIN_HITS) {
    const words = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([word]) => word).slice(0, 4);
    lines.push(`이 표현은 쓰지 마세요: ${words.join(', ')}.`);
  }
  return lines.join('\n');
};

/**
 * 고치기 목표 바이트. 서버는 목표의 105%까지 허용하므로, 그 상한이 지금 글 길이와 NEIS 한도를 넘지 않게 낮춘다.
 */
export const overlapFixTargetBytes = (text = '', limit = 0) => {
  const current = calculateNEISBytes(text);
  const ceiling = limit > 0 ? Math.min(limit, current) : current;
  return Math.max(100, Math.floor(ceiling / 1.05));
};

export const SEVERITY_NOTE = {
  audit: '10명 넘게 같은 문장이에요. 교육청 감사에서 지적된 규모예요.',
  national: '반 70% 넘게 같은 문장이에요. 감사원이 지적한 규모예요.',
};
